import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Dynamic API URL - use current origin for API calls (works on any deployed domain)
const API_URL = window.location.origin;

// Emergent Auth configuration
const EMERGENT_AUTH_BASE_URL = 'https://demobackend.emergentagent.com/auth/v1/env';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is authenticated on mount
  const checkAuth = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build headers - include session token from localStorage as fallback
      const headers = {};
      const storedToken = localStorage.getItem('auth_session_token');
      if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }
      
      console.log('[AuthContext] Checking auth, has stored token:', !!storedToken);
      
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include',
        headers,
      });
      
      console.log('[AuthContext] Auth check response status:', response.status);
      
      if (response.ok) {
        const userData = await response.json();
        console.log('[AuthContext] User authenticated:', userData.email, 'is_admin:', userData.is_admin);
        setUser(userData);
        
        // Sync to localStorage for AdminContentEditor compatibility
        if (userData.email) {
          localStorage.setItem('soiree_email', userData.email);
          if (userData.is_admin) {
            localStorage.setItem('soiree_approved', 'true');
          }
        }
      } else {
        console.log('[AuthContext] Auth check failed, clearing user');
        setUser(null);
        // Don't clear localStorage token here - might just be a temporary network issue
      }
    } catch (err) {
      console.error('[AuthContext] Auth check error:', err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Initiate OAuth login
  const login = useCallback((returnUrl = window.location.href) => {
    // Store the return URL in sessionStorage
    sessionStorage.setItem('auth_return_url', returnUrl);
    
    // Build the callback URL (where Emergent Auth will redirect after login)
    const callbackUrl = `${window.location.origin}/auth/callback`;
    
    // Redirect to Emergent Auth - use 'redirect' parameter
    const authUrl = `${EMERGENT_AUTH_BASE_URL}/oauth?redirect=${encodeURIComponent(callbackUrl)}`;
    window.location.href = authUrl;
  }, []);

  // Handle the OAuth callback
  const handleCallback = useCallback(async (sessionId) => {
    try {
      setLoading(true);
      setError(null);

      // Exchange session_id for session_token
      const response = await fetch(`${API_URL}/api/auth/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId }),
      });

      if (!response.ok) {
        // Try to get error details
        let errorMsg = 'Authentication failed';
        try {
          const errorData = await response.json();
          errorMsg = errorData.detail || errorMsg;
        } catch (e) {
          // Response might not be JSON
        }
        throw new Error(errorMsg);
      }

      const data = await response.json();
      setUser(data.user);
      
      // Store session token and user info in localStorage as backup
      if (data.session_token) {
        localStorage.setItem('auth_session_token', data.session_token);
      }
      if (data.user) {
        localStorage.setItem('soiree_email', data.user.email);
        if (data.user.is_admin) {
          localStorage.setItem('soiree_approved', 'true');
        }
      }
      
      return data.user;
    } catch (err) {
      console.error('Auth callback failed:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Logout
  const logout = useCallback(async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      // Clear any stored auth data
      localStorage.removeItem('soiree_email');
      localStorage.removeItem('soiree_approved');
      localStorage.removeItem('frontrow_email');
      localStorage.removeItem('frontrow_approved');
    }
  }, []);

  // Check if user has access to a specific page
  const checkPageAccess = useCallback(async (pageId) => {
    if (!user) return { has_access: false, reason: 'not_authenticated' };
    
    try {
      const response = await fetch(`${API_URL}/api/auth/check-access/${pageId}`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        return await response.json();
      }
      return { has_access: false, reason: 'error' };
    } catch (err) {
      console.error('Access check failed:', err);
      return { has_access: false, reason: 'error' };
    }
  }, [user]);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || false,
    login,
    logout,
    handleCallback,
    checkAuth,
    checkPageAccess,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
